import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { settingsUrl } from "@presentation/common/settingsRoutes";
import { Button } from "@progress/kendo-react-buttons";
import { Card, CardBody } from "@progress/kendo-react-layout";
import { Grid, GridColumn, GridCustomCellProps, GridDetailRowProps, GridExpandChangeEvent } from "@progress/kendo-react-grid";
import { folderIcon, gearIcon, gridIcon, pencilIcon, trashIcon } from "@progress/kendo-svg-icons";
import { useInjection } from "@presentation/components/dependencyInjection";
import { TeamManagementService } from "@application/useCases/workspace/TeamManagementService";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { usePermissionStore } from "@presentation/stores/PermissionStore";
import { useTeamPermission } from "@presentation/components/helpers/usePermissionCheck";
import { CreateTeamDialog } from "@presentation/components/team/CreateTeamDialog";
import { RenameTeamDialog } from "@presentation/components/team/RenameTeamDialog";
import { DeleteTeamDialog } from "@presentation/components/team/DeleteTeamDialog";
import { GatedActionButton } from "@presentation/components/permission/GatedActionButton";
import { formatUserDisplayName, useUserDisplayNames } from "@presentation/hooks/useUserDisplayNames";
import { DATA_ITEM_KEY, EXPANDED_FIELD } from "@presentation/components/helpers/constants";
import { fillCardStyle, mutedTextStyle, pageFillContentStyle, pageHeaderRegionStyle, pageHeaderStyle, pageShellStyle, pageTitleStyle, tableRowActionsStyle } from "@presentation/common/pageLayout";
import { TEAM_ROLE_RANK } from "@presentation/pages/TeamDetailPage";
import { ADMIN_ROLE_NAME, TeamActions } from "@domain/schemas/workspace";
import type { MyTeam, TeamMembership } from "@domain/schemas/workspace";
import { useIsEntitled } from "@presentation/hooks/useEntitlements";
import { EntitlementLockedBadge } from "@presentation/components/permission/EntitlementLockedBadge";
import { CONTEXT_MANAGEMENT_ENTITLEMENT_NAME } from "@domain/valueObjects/contextManagementEntitlement";
import { DefaultContextBadge } from "@presentation/components/workspace/DefaultContextBadge";
import { useWorkspaceFilters } from "@presentation/hooks/useWorkspaceFilters";

/**
A team's members, shown when its row is expanded - fetched lazily on first expand since the teams
list only carries the caller's own role, not the full membership list.
*/
const TeamMembersDetail: React.FC<GridDetailRowProps> = ({ dataItem }) => {
  const teamManagementService = useInjection(TeamManagementService);
  const [memberships, setMemberships] = useState<TeamMembership[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;
    void (async () => {
      try {
        const detail = await teamManagementService.getTeamAsync(dataItem.id);
        if (!isCancelled) setMemberships(detail.memberships);
      } catch {
        if (!isCancelled) setError("Could not load members.");
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [dataItem.id, teamManagementService]);

  const userNames = useUserDisplayNames(React.useMemo(() => memberships?.map((membership) => membership.userId) ?? [], [memberships]));

  const sortedMemberships = React.useMemo(() => {
    if (!memberships) return [];
    return memberships.toSorted((left, right) => {
      const rankDifference = TEAM_ROLE_RANK[left.role] - TEAM_ROLE_RANK[right.role];
      return rankDifference === 0 ? formatUserDisplayName(userNames[left.userId], left.userId).localeCompare(formatUserDisplayName(userNames[right.userId], right.userId)) : rankDifference;
    });
  }, [memberships, userNames]);

  if (error) return <p style={{ margin: 0, padding: "4px 16px 16px", color: "#7f1d1d" }}>{error}</p>;
  if (!memberships) return <p style={{ margin: 0, padding: "4px 16px 16px", color: "#64748b" }}>Loading members...</p>;
  if (memberships.length === 0) return <p style={{ margin: 0, padding: "4px 16px 16px", color: "#64748b" }}>No members in this team.</p>;

  return (
    <div style={{ padding: "4px 16px 16px" }}>
      <Grid data={sortedMemberships} dataItemKey="id">
        <GridColumn title="Member" cell={(properties: GridCustomCellProps) => <td>{formatUserDisplayName(userNames[properties.dataItem.userId], properties.dataItem.userId)}</td>} />
        <GridColumn field="role" title="Role" width="160px" />
      </Grid>
    </div>
  );
};

type TeamRowDialogState = { kind: "none" } | { kind: "rename" } | { kind: "delete" };

/**
Per-row navigation/management actions for a team - kept as its own component (not an inline cell
renderer) so `useTeamPermission` can be called once per row, the same as any other component.
Renders just its buttons (no `<td>`/wrapper of its own) so the parent cell can lay out every
action button - these included - in one consistently-spaced row; its dialogs render alongside as
fragment siblings, same as any other portal-backed Kendo `Dialog`.
*/
const TeamRowActions: React.FC<{ team: MyTeam; isAdmin: boolean; entitlementActive: boolean | null; onRenamed: (teamId: string, name: string) => void; onDeleted: (teamId: string) => void }> = ({
  team,
  isAdmin,
  entitlementActive,
  onRenamed,
  onDeleted,
}) => {
  const renameTeamPermission = useTeamPermission(team.id, TeamActions.RenameTeam, team.isDefault);
  const [dialog, setDialog] = useState<TeamRowDialogState>({ kind: "none" });

  const handleDelete = () => {
    onDeleted(team.id);
  };

  return (
    <>
      <GatedActionButton
        type="button"
        fillMode="flat"
        svgIcon={pencilIcon}
        title="Rename team"
        aria-label="Rename team"
        permission={renameTeamPermission}
        onClick={() => setDialog({ kind: "rename" })}
      />
      {isAdmin && (
        <GatedActionButton
          type="button"
          fillMode="flat"
          themeColor="error"
          svgIcon={trashIcon}
          title="Delete team"
          aria-label="Delete team"
          permission={{ allowed: true, reason: null }}
          entitlementActive={entitlementActive}
          onClick={() => setDialog({ kind: "delete" })}
        />
      )}
      {dialog.kind === "rename" && <RenameTeamDialog teamId={team.id} currentName={team.name} onClose={() => setDialog({ kind: "none" })} onSuccess={(updated) => onRenamed(team.id, updated.name)} />}
      {dialog.kind === "delete" && <DeleteTeamDialog teamId={team.id} teamName={team.name} onClose={() => setDialog({ kind: "none" })} onSuccess={handleDelete} />}
    </>
  );
};

/**
Team list (`/teams`) - each row expands to show its members, with actions for projects, jobs, and scoped Settings.
*/
const TeamsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { teamId: filterTeamId } = useWorkspaceFilters();
  const teams = useWorkspaceContextStore((state) => state.teams);
  const projects = useWorkspaceContextStore((state) => state.projects);
  const setWorkspace = useWorkspaceContextStore((state) => state.setWorkspace);
  const isAdmin = usePermissionStore((state) => state.isAdmin);
  const contextManagementEntitled = useIsEntitled(CONTEXT_MANAGEMENT_ENTITLEMENT_NAME);
  const [teamSearchTerm, setTeamSearchTerm] = useState("");
  const [isCreateTeamOpen, setIsCreateTeamOpen] = useState(false);
  const [expandedTeamIds, setExpandedTeamIds] = useState<ReadonlySet<string>>(new Set());

  const handleTeamRenamed = (teamId: string, name: string) => {
    setWorkspace(
      teams.map((team) => (team.id === teamId ? { ...team, name } : team)),
      projects,
    );
  };

  const handleTeamDeleted = (teamId: string) => {
    setWorkspace(
      teams.filter((team) => team.id !== teamId),
      projects.filter((project) => project.teamId !== teamId),
    );
  };

  const teamRows = React.useMemo(() => {
    const normalizedTerm = teamSearchTerm.trim().toLowerCase();
    return (
      teams
        .filter((team) => !filterTeamId || team.id === filterTeamId)
        .filter((team) => !normalizedTerm || team.name.toLowerCase().includes(normalizedTerm))
        // Only an Admin can see a team with no role of their own (every other caller's "my teams"
        // rows always have one) - "-" marks that rather than fabricating a role.
        .map((team) => ({ ...team, role: team.role ?? "-", [EXPANDED_FIELD]: expandedTeamIds.has(team.id) }))
    );
  }, [expandedTeamIds, teamSearchTerm, teams, filterTeamId]);

  const handleExpandChange = (event: GridExpandChangeEvent) => {
    const teamId = event.dataItem.id;
    setExpandedTeamIds((current) => {
      const next = new Set(current);
      if (event.value) next.add(teamId);
      else next.delete(teamId);
      return next;
    });
  };

  return (
    <div style={pageShellStyle}>
      <div style={pageHeaderRegionStyle}>
        <div style={pageHeaderStyle}>
          <h1 style={pageTitleStyle}>Teams</h1>
          {isAdmin && (
            <GatedActionButton type="button" themeColor="primary" permission={{ allowed: true, reason: null }} entitlementActive={contextManagementEntitled} onClick={() => setIsCreateTeamOpen(true)}>
              Create team
            </GatedActionButton>
          )}
        </div>
      </div>

      <div style={pageFillContentStyle}>
        <Card style={fillCardStyle}>
          <CardBody style={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column", padding: 0 }}>
            <input
              type="search"
              className="project-sidebar-search"
              placeholder="Search teams"
              value={teamSearchTerm}
              onChange={(event) => setTeamSearchTerm(event.target.value)}
              style={{ minWidth: 220, flex: "0 0 auto", margin: "16px 16px 12px" }}
            />
            {teamRows.length > 0 ? (
              <Grid
                style={{ flex: "1 1 auto", minHeight: 0 }}
                data={teamRows}
                dataItemKey={DATA_ITEM_KEY}
                expandField={EXPANDED_FIELD}
                onExpandChange={handleExpandChange}
                detail={TeamMembersDetail}
                sortable={true}
              >
                <GridColumn
                  field="name"
                  title="Team"
                  cell={(properties: GridCustomCellProps) => (
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        {properties.dataItem.name}
                        {properties.dataItem.isDefault && <DefaultContextBadge context="team" />}
                        {!properties.dataItem.isDefault && contextManagementEntitled === false && <EntitlementLockedBadge />}
                      </span>
                    </td>
                  )}
                />
                <GridColumn field="role" title="Your role" width="140px" />
                <GridColumn
                  title=""
                  width="230px"
                  cell={(properties: GridCustomCellProps) => (
                    <td>
                      <div style={tableRowActionsStyle}>
                        <Button
                          type="button"
                          fillMode="flat"
                          svgIcon={folderIcon}
                          title="View projects"
                          aria-label="View projects"
                          onClick={() => navigate(`/projects?teamId=${properties.dataItem.id}`)}
                        />
                        <Button type="button" fillMode="flat" svgIcon={gridIcon} title="Team jobs" aria-label="Team jobs" onClick={() => navigate(`/jobs?teamId=${properties.dataItem.id}`)} />
                        <Button
                          type="button"
                          fillMode="flat"
                          svgIcon={gearIcon}
                          title="Team settings"
                          aria-label="Team settings"
                          onClick={() => navigate(settingsUrl({ level: "team", teamId: properties.dataItem.id }))}
                        />
                        <TeamRowActions team={properties.dataItem} isAdmin={isAdmin} entitlementActive={contextManagementEntitled} onRenamed={handleTeamRenamed} onDeleted={handleTeamDeleted} />
                      </div>
                    </td>
                  )}
                />
              </Grid>
            ) : (
              <p style={mutedTextStyle}>No teams match.</p>
            )}
          </CardBody>
        </Card>
      </div>

      {isCreateTeamOpen && (
        <CreateTeamDialog
          onClose={() => setIsCreateTeamOpen(false)}
          onSuccess={(created) => {
            const workspace = useWorkspaceContextStore.getState();
            setWorkspace([...workspace.teams, { id: created.id, name: created.name, isDefault: created.isDefault, role: ADMIN_ROLE_NAME }], workspace.projects);
            setTeamSearchTerm("");
            navigate(`/teams?scope=team&teamId=${encodeURIComponent(created.id)}`);
          }}
        />
      )}
    </div>
  );
};

export default TeamsListPage;
